/* global YT */
/**
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 * @param {object} Swiper - The Swiper library.
 */
(function (Drupal, once, Swiper) {
  'use strict';

  let youtubePlayer;

  /**
   * Manage focus for slides - prevent tabbing into hidden slides.
   *
   * @param {object} swiperInstance - Swiper instance.
   */
  function updateSlidesInert(swiperInstance) {
    if ('inert' in HTMLElement.prototype) {
      swiperInstance.slides.forEach((slide, idx) => {
        slide.inert = idx !== swiperInstance.activeIndex;
      });
    }
  }

  /**
   * Apply roving tabindex on pagination bullets.
   *
   * @param {HTMLElement} pagination - Pagination container element.
   * @param {number} activeIndex - Index of the currently active bullet.
   */
  function updatePaginationTabindex(pagination, activeIndex) {
    const bullets = pagination.querySelectorAll('.swiper-pagination-bullet');
    bullets.forEach((bullet, idx) => {
      const isActive = idx === activeIndex;
      bullet.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive) {
        bullet.setAttribute('aria-current', 'true');
      }
      else {
        bullet.removeAttribute('aria-current');
      }
    });
  }

  function setupAudioPlayer(slide) {
    const audioContainer = slide.querySelector('.gallery-slide__audio-file');
    const playButton = slide.querySelector('.gallery-slide__audio-play-button');

    if (!audioContainer || !playButton) {
      return;
    }

    const audio = audioContainer.querySelector('audio');
    if (!audio) {
      return;
    }

    if (audioContainer.id) {
      playButton.setAttribute('aria-controls', audioContainer.id);
    }

    // Initially hide the audio player
    audioContainer.classList.add('gallery-slide__audio-file--is-hidden');
    audioContainer.setAttribute('aria-hidden', 'true');
    playButton.setAttribute('aria-expanded', 'false');

    let autoHideTimerId = null;

    function clearAutoHide() {
      if (autoHideTimerId !== null) {
        window.clearTimeout(autoHideTimerId);
        autoHideTimerId = null;
      }
    }

    function scheduleAutoHide() {
      clearAutoHide();
      autoHideTimerId = window.setTimeout(() => {
        audioContainer.classList.add('gallery-slide__audio-file--is-auto-hidden');
      }, 4000);
    }

    audio.addEventListener('play', () => {
      audioContainer.classList.remove('gallery-slide__audio-file--is-auto-hidden');
      scheduleAutoHide();

      // Track audio play event.
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'Audio Event',
          audio_title: audio.title || audio.getAttribute('aria-label') || 'Audio File',
          audio_src: audio.src || audio.currentSrc,
          audio_status: 'play',
          audio_current_time: Math.round(audio.currentTime || 0),
          media_provider: 'audio'
        });
      }
    });

    audio.addEventListener('pause', () => {
      clearAutoHide();
      audioContainer.classList.remove('gallery-slide__audio-file--is-auto-hidden');

      // Track audio pause event.
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'Audio Event',
          audio_title: audio.title || audio.getAttribute('aria-label') || 'Audio File',
          audio_src: audio.src || audio.currentSrc,
          audio_status: 'pause',
          audio_current_time: Math.round(audio.currentTime || 0),
          media_provider: 'audio'
        });
      }
    });

    audio.addEventListener('ended', () => {
      clearAutoHide();
      audioContainer.classList.remove('gallery-slide__audio-file--is-auto-hidden');

      // Track audio ended event.
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'Audio Event',
          audio_title: audio.title || audio.getAttribute('aria-label') || 'Audio File',
          audio_src: audio.src || audio.currentSrc,
          audio_status: 'ended',
          audio_duration: Math.round(audio.duration || 0),
          media_provider: 'audio'
        });
      }
    });

    let activatedByKeyboard = false;

    playButton.addEventListener('pointerdown', () => {
      activatedByKeyboard = false;
    });

    playButton.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        activatedByKeyboard = true;
      }
    });

    // Handle play button click
    playButton.addEventListener('click', () => {
      playButton.blur();
      audioContainer.classList.remove('gallery-slide__audio-file--is-hidden');
      audioContainer.setAttribute('aria-hidden', 'false');
      playButton.setAttribute('aria-expanded', 'true');
      playButton.classList.add('gallery-slide__audio-play-button--is-hidden');

      audio.controls = true;
      const shouldFocusAudio = activatedByKeyboard;
      activatedByKeyboard = false;

      if (shouldFocusAudio) {
        audio.focus();
      }
      else {
        window.setTimeout(() => {
          if (document.activeElement === audio) {
            audio.blur();
          }
        }, 0);
      }

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
        });
      }
    });
  }

  function setupVideoPlayer(slide) {
    const playButton = slide.querySelector('.gallery-slide__play-button');
    const mediaWrapper = slide.querySelector('.gallery-slide__media-wrapper');

    if (!playButton || !mediaWrapper) {
      return;
    }

    // Check for YouTube iframe.
    const youtubeIframe = mediaWrapper.querySelector('.gallery-slide__video-iframe');
    if (youtubeIframe) {
      // Set up YouTube API tracking for analytics.
      setupYouTubeTracking(youtubeIframe, mediaWrapper);
      // Hide the play button since YouTube iframe has its own controls.
      playButton.style.display = 'none';
      return;
    }

    const videoUrl = mediaWrapper.getAttribute('data-video-url');
    if (!videoUrl) {
      return;
    }

    playButton.addEventListener('click', () => {
      // For YouTube URLs with enablejsapi=1, we can still create a player.
      if (videoUrl.includes('youtube.com/embed/') && videoUrl.includes('enablejsapi=1')) {
        const videoId = videoUrl.match(/embed\/([^?]+)/)?.[1];
        if (videoId && window.YT && window.YT.Player) {
          let videoContainer = mediaWrapper.querySelector('.gallery-slide__video-container');
          if (!videoContainer) {
            videoContainer = document.createElement('div');
            videoContainer.classList.add('gallery-slide__video-container');
            mediaWrapper.appendChild(videoContainer);
          }

          youtubePlayer = new window.YT.Player(videoContainer, {
            videoId: videoId,
            playerVars: {
              autoplay: 1,
              enablejsapi: 1,
              rel: 0
            }
          });
          mediaWrapper.youtubePlayerInstance = youtubePlayer;
        }
        else {
          // Fallback: just create iframe if YT API not available.
          let iframe = mediaWrapper.querySelector('iframe');
          if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.src = videoUrl + '&autoplay=1&rel=0';
            iframe.frameBorder = '0';
            iframe.allowFullscreen = true;
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.classList.add('gallery-slide__video-iframe');
            mediaWrapper.appendChild(iframe);
          }
        }
      }
    });
  }

  function setupYouTubeTracking(iframe, mediaWrapper) {
    const videoId = mediaWrapper.getAttribute('data-video-id');
    const videoUrl = mediaWrapper.getAttribute('data-video-url');

    if (!videoId || !videoUrl) {
      return;
    }

    // Wait for YouTube API to be ready.
    function initYouTubePlayer(retryCount = 0) {
      const maxRetries = 50; // Maximum 5 seconds (50 * 100ms).

      if (typeof YT === 'undefined' || !YT.Player) {
        if (retryCount >= maxRetries) {
          return;
        }
        // YouTube API not loaded yet, try again in 100ms.
        setTimeout(() => initYouTubePlayer(retryCount + 1), 100);
        return;
      }

      // Enhance the existing Twig-rendered iframe in-place (it already has
      // enablejsapi=1). This avoids creating a second iframe which would
      // break responsive sizing and cause focus/tab issues.
      if (!iframe.id) {
        iframe.id = 'youtube-player-' + videoId + '-' + Date.now();
      }

      const player = new YT.Player(iframe.id);

      // Store player reference for cleanup and pause on slide change.
      mediaWrapper.youtubePlayerInstance = player;
    }

    // Load YouTube API if not already loaded.
    if (typeof YT === 'undefined' &&
      !document.querySelector('script[src*="youtube.com/iframe_api"]')
    ) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // Set up callback for when API is ready.
      window.onYouTubeIframeAPIReady = function () {
        // Process all pending YouTube players.
        document.querySelectorAll('.gallery-slide__video-iframe').forEach(iframe => {
          const wrapper = iframe.closest('.gallery-slide__media-wrapper');
          if (wrapper && !wrapper.youtubePlayerInstance) {
            setupYouTubeTracking(iframe, wrapper);
          }
        });
      };
    }
    else {
      initYouTubePlayer();
    }
  }

  function announceSlideChange(gallery, current, total, message) {
    const announcement = gallery.closest('.gallery').querySelector('.gallery__sr-announcement');
    if (announcement) {
      announcement.textContent = message || `Slide ${current} of ${total}`;
    }
  }

  /**
   * Update the state of navigation buttons based on the current slide.
   * @param {*} swiper - The Swiper instance.
   * @param {*} gallery - The gallery element.
   */
  function updateNavigationState(swiper, gallery) {
    const prevButton = gallery.querySelector('.gallery__navigation--prev');
    const nextButton = gallery.querySelector('.gallery__navigation--next');
    const {activeIndex} = swiper;
    const totalSlides = swiper.slides.length;

    if (totalSlides <= 1 || !prevButton || !nextButton) {
      return;
    }

    const focusedElement = document.activeElement;
    const isFirst = activeIndex === 0;
    const isLast = activeIndex === totalSlides - 1;

    const setDisabled = (button, disabled, fallback) => {
      button.toggleAttribute('disabled', disabled);
      button.setAttribute('aria-disabled', String(disabled));
      button.setAttribute('tabindex', disabled ? '-1' : '0');
      if (disabled && focusedElement === button) {
        fallback.focus();
      }
    };

    setDisabled(prevButton, isFirst, nextButton);
    setDisabled(nextButton, isLast, prevButton);
  }

  Drupal.behaviors.galleryBehavior = {
    attach: function (context) {
      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        return;
      }

      // Check if user prefers reduced motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      once('gallery-behavior', '.gallery > .swiper', context).forEach(gallery => {
        const swiper = new Swiper(gallery, {
          loop: false,
          slidesPerView: 1,
          centeredSlides: true,
          spaceBetween: 24,
          watchSlidesProgress: true,
          speed: prefersReducedMotion ? 0 : 300,

          navigation: {
            nextEl: gallery.querySelector('.gallery__navigation--next'),
            prevEl: gallery.querySelector('.gallery__navigation--prev')
          },

          pagination: {
            el: gallery.querySelector('.gallery__pagination'),
            clickable: true,
            type: 'custom',
            renderCustom: function (swiper, current, total) {
              let bullets = '';
              for (let itr = 1; itr <= total; itr++) {
                const activeClass = itr === current ? ' swiper-pagination-bullet-active' : '';
                const tabindex = itr === current ? '0' : '-1';
                const ariaCurrent = itr === current ? ' aria-current="true"' : '';
                bullets += `<span class="swiper-pagination-bullet${activeClass}" role="tab" aria-label="Go to slide ${itr}" tabindex="${tabindex}"${ariaCurrent} data-slide="${itr}"></span>`;
              }

              // Only show separator and counter when there are more than 5 slides
              let separatorAndCounter = '';
              if (total > 5) {
                const currentFormatted = String(current).padStart(2, '0');
                const totalFormatted = String(total).padStart(2, '0');

                separatorAndCounter = `
                  <div class="gallery__pagination-separator"></div>
                  <div class="gallery__pagination-counter">
                    <span class="gallery__pagination-current">${currentFormatted}</span><span class="gallery__pagination-total">/${totalFormatted}</span>
                  </div>
                `;
              }

              return `
                <div class="swiper-pagination-bullets">${bullets}</div>
                ${separatorAndCounter}
              `;
            }
          },

          on: {
            slideChange(swiperInstance) {
              const previousSlide = swiperInstance.slides[swiperInstance.previousIndex];

              // Handle YouTube players with API.
              const previousMediaWrapper = previousSlide.querySelector('.gallery-slide__media-wrapper');
              if (previousMediaWrapper && previousMediaWrapper.youtubePlayerInstance) {
                try {
                  previousMediaWrapper.youtubePlayerInstance.pauseVideo();
                }
                catch (e) {
                  // Handled by postMessage fallback below.
                }
              }

              // PostMessage fallback for YouTube iframes (covers race conditions
              // where youtubePlayerInstance is not yet ready).
              previousSlide.querySelectorAll('iframe[src*="youtube.com"]').forEach(iframe => {
                try {
                  iframe.contentWindow.postMessage(
                    JSON.stringify({event: 'command', func: 'pauseVideo', args: ''}),
                    '*'
                  );
                }
                catch (e) {
                  // Ignore cross-origin errors.
                }
              });

              // Handle Vimeo videos (legacy).
              const videoFramePrevious = previousSlide.querySelector('iframe[src*="vimeo.com/"]');
              if (videoFramePrevious && window.Vimeo && window.Vimeo.Player) {
                try {
                  const player = new window.Vimeo.Player(videoFramePrevious);
                  player.setCurrentTime(0);
                  player.pause();
                }
                catch (e) {
                  window.console.error('Failed to reset Vimeo player:', e);
                }
              }

              // Handle audio elements.
              const previousSlideAudios = previousSlide.querySelectorAll('audio');
              if (previousSlideAudios.length) {
                previousSlideAudios.forEach((audio) => {
                  audio.pause();

                  if (Number.isFinite(audio.currentTime)) {
                    audio.currentTime = 0;
                  }
                });
              }

              const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
              const videoFrameActive = activeSlide.querySelector('iframe[src*="vimeo.com/"]');
              if (videoFrameActive && swiperInstance.realIndex !== 0 && window.Vimeo && window.Vimeo.Player) {
                try {
                  const player = new window.Vimeo.Player(videoFrameActive);
                  player.play();
                }
                catch (e) {
                  window.console.error('Failed to play Vimeo video on active slide:', e);
                }
              }

              // Prevent tabbing into hidden slides
              updateSlidesInert(swiperInstance);

              // Update pagination roving tabindex
              const pagination = gallery.querySelector('.gallery__pagination');
              if (pagination) {
                updatePaginationTabindex(pagination, swiperInstance.activeIndex);
              }

              // Update navigation button states.
              updateNavigationState(swiperInstance, gallery);

              // Announce slide change to screen readers
              const currentSlide = swiperInstance.realIndex + 1;
              const totalSlides = swiperInstance.slides.length;
              announceSlideChange(gallery, currentSlide, totalSlides);
            },

            afterInit(swiperInstance) {
              // Prevent tabbing into hidden slides
              updateSlidesInert(swiperInstance);

              // Set initial pagination roving tabindex
              const pagination = gallery.querySelector('.gallery__pagination');
              if (pagination) {
                updatePaginationTabindex(pagination, swiperInstance.activeIndex);
              }

              // Update navigation button states.
              updateNavigationState(swiperInstance, gallery);

              // Announce initial slide
              announceSlideChange(gallery, 1, swiperInstance.slides.length);
            }
          }
        });

        // Announce boundary messages on navigation buttons.
        const prevBtn = gallery.querySelector('.gallery__navigation--prev');
        const nextBtn = gallery.querySelector('.gallery__navigation--next');
        if (prevBtn) {
          prevBtn.addEventListener('click', () => {
            if (swiper.isBeginning) {
              announceSlideChange(gallery, 0, 0, Drupal.t('This is the first slide'));
            }
          });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            if (swiper.isEnd) {
              announceSlideChange(gallery, 0, 0, Drupal.t('This is the last slide'));
            }
          });
        }

        swiper.slides.forEach(setupVideoPlayer);
        swiper.slides.forEach(setupAudioPlayer);

        // Arrow key navigation when the gallery container itself has focus.
        gallery.addEventListener('keydown', (event) => {
          if (event.target !== gallery) {
            return;
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            swiper.slideNext();
          }
          else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            swiper.slidePrev();
          }
        });

        // Add click handlers for custom pagination bullets
        const pagination = gallery.querySelector('.gallery__pagination');
        if (pagination) {
          pagination.addEventListener('click', (event) => {
            const bullet = event.target.closest('.swiper-pagination-bullet[data-slide]');
            if (bullet) {
              event.preventDefault();
              const slideIndex = parseInt(bullet.getAttribute('data-slide'), 10) - 1;
              swiper.slideTo(slideIndex);
              // Re-query the active bullet from fresh DOM after renderCustom
              const activeBullet = pagination.querySelector('.swiper-pagination-bullet-active');
              if (activeBullet) {
                activeBullet.focus();
              }
            }
          });

          // Add keyboard support for pagination bullets
          pagination.addEventListener('keydown', (event) => {
            const bullet = event.target.closest('.swiper-pagination-bullet[data-slide]');
            if (!bullet) {
              return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              const slideIndex = parseInt(bullet.getAttribute('data-slide'), 10) - 1;
              swiper.slideTo(slideIndex);
              return;
            }

            if (event.key === 'ArrowRight') {
              event.preventDefault();
              swiper.slideNext();
              const activeBullet = pagination.querySelector('.swiper-pagination-bullet-active');
              if (activeBullet) {
                activeBullet.focus();
              }
              return;
            }

            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              swiper.slidePrev();
              const activeBullet = pagination.querySelector('.swiper-pagination-bullet-active');
              if (activeBullet) {
                activeBullet.focus();
              }
            }
          });
        }
      });
    },
    detach: function (context) {
      if (youtubePlayer) {
        try {
          youtubePlayer.destroy();
        }
        catch (e) {
          window.console.error('Failed to destroy YouTube player instance:', e);
        }
      }

      // Clean up all YouTube player instances.
      once.remove('gallery-behavior', '.gallery > .swiper', context).forEach(gallery => {
        gallery.querySelectorAll('.gallery-slide__media-wrapper').forEach(wrapper => {
          if (wrapper.youtubePlayerInstance) {
            try {
              wrapper.youtubePlayerInstance.destroy();
            }
            catch (e) {
              window.console.error('Failed to clean up YouTube player instance:', e);
            }
          }
        });
      });
    }
  };
})(Drupal, once, window.Swiper);
